import { api } from './client.ts';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  pictureUrl: string | null;
  creditBalance: number;
  role: string;
  preferredLanguage: string;
  createdAt: string;
}

export interface RegisterResponse {
  userId: string;
  email: string;
  /**
   * When true, the user must click the link in their inbox before signing in. When false,
   * the backend already issued the auth cookie — the SPA should fetch /auth/me and route to
   * home. Controlled server-side by the Auth:RequireEmailVerification config flag.
   */
  emailVerificationRequired: boolean;
}

export interface PasswordSignInResponse {
  id: string;
  email: string;
  displayName: string | null;
  creditBalance: number;
  role: string;
}

export const authApi = {
  /** Returns the current user or null if not signed in. */
  async me(): Promise<AuthUser | null> {
    try {
      return await api<AuthUser>('/api/auth/me');
    } catch (e) {
      // 401 just means no session — surface as null, not an error.
      if (e instanceof Error && 'status' in e && (e as { status: number }).status === 401) {
        return null;
      }
      throw e;
    }
  },

  /** Redirects the browser to Google OAuth. The success URL comes back to returnUrl. */
  startGoogleSignIn(returnUrl = '/'): void {
    const url = `/api/auth/login/google?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = url;
  },

  async signOut(): Promise<void> {
    await api<void>('/api/auth/sign-out', { method: 'POST' });
  },

  /** Register with email + password. 202 on success — user must verify email before sign-in. */
  async register(email: string, password: string, displayName?: string): Promise<RegisterResponse> {
    return api<RegisterResponse>('/api/auth/register', {
      method: 'POST',
      body: { email, password, displayName: displayName || undefined },
    });
  },

  /** Sign in with email + password. Issues the auth cookie on success. */
  async signInWithPassword(email: string, password: string): Promise<PasswordSignInResponse> {
    return api<PasswordSignInResponse>('/api/auth/sign-in', {
      method: 'POST',
      body: { email, password },
    });
  },

  /** Consume the verification token. Signs the user in on success. */
  async verifyEmail(token: string): Promise<PasswordSignInResponse> {
    return api<PasswordSignInResponse>('/api/auth/verify-email', {
      method: 'POST',
      body: { token },
    });
  },

  /** Request a fresh verification email. Always 202 (no enumeration signal). */
  async resendVerification(email: string): Promise<void> {
    await api<void>('/api/auth/resend-verification', {
      method: 'POST',
      body: { email },
    });
  },

  /** Kick off a password reset. Always 202 (no enumeration signal). */
  async forgotPassword(email: string): Promise<void> {
    await api<void>('/api/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  },

  /** Consume the reset token and set a new password. Issues the auth cookie on success. */
  async resetPassword(token: string, password: string): Promise<PasswordSignInResponse> {
    return api<PasswordSignInResponse>('/api/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    });
  },
};
